import { Box } from "@mui/material";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import React, { useEffect, useRef } from "react";
import {
  MapContainer,
  Pane,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";
import { FacilityData } from "../../types/facilityTypes";
import CustomZoomControls from "./CustomZoomControls";
import MapLegend from "./MapLegend";

// Import leaflet.glify - it extends the L object
// @ts-ignore - leaflet.glify doesn't have TypeScript definitions
const glify = require("leaflet.glify");

// Extend Leaflet namespace to include glify
declare module "leaflet" {
  namespace glify {
    function points(options: any): any;
  }
}

// Shared ref to track if a point was clicked (to prevent map click from firing)
const pointClickedRef = { current: false };

// Map Click Handler Component - only fires if no point was clicked
const MapClickHandler: React.FC<{ onMapClick: () => void }> = ({
  onMapClick,
}) => {
  useMapEvents({
    click: () => {
      // Only fire if a point wasn't just clicked
      if (!pointClickedRef.current) {
        onMapClick();
      }
    },
  });
  return null;
};

// WebGL Points Layer Component using Leaflet.glify
const GlifyPointsLayer: React.FC<{
  entities: FacilityData[];
  onEntityClick: (entity: FacilityData) => void;
}> = ({ entities, onEntityClick }) => {
  const map = useMap();
  const glifyLayerRef = useRef<any>(null);
  const tooltipRef = useRef<L.Popup | null>(null);

  useEffect(() => {
    if (!map || entities.length === 0) return;

    // Check if glify is available
    if (!L.glify || !L.glify.points) {
      return;
    }

    let unmounted = false;

    // Remove the glify layer AND cancel the CanvasOverlay's pending
    // requestAnimationFrame. The Points constructor queues a redraw frame
    // (setup().render() -> layer.redraw()) BEFORE our _redraw guard below is
    // installed, and Leaflet's requestAnimFrame captures the function
    // reference at schedule time — so that one frame bypasses any
    // after-the-fact wrapping. glify's onRemove never cancels it; if the map
    // unmounts before it fires, _redraw derefs a nulled _map and throws
    // "Cannot read properties of null (reading 'getSize')" (Sentry
    // FRONTEND-5). Cancelling the frame at teardown closes that window.
    const removeGlifyLayer = () => {
      const wrapper = glifyLayerRef.current;
      if (!wrapper) return;
      const overlay = wrapper.layer;
      if (overlay && overlay._frame != null) {
        L.Util.cancelAnimFrame(overlay._frame);
        overlay._frame = null;
      }
      wrapper.remove();
      glifyLayerRef.current = null;
    };

    // Defer glify initialization to the next animation frame so the map
    // container is fully laid out and getSize() returns a valid value.
    const initGlify = () => {
      if (unmounted || !map.getContainer()) return;

      // Remove existing layer if it exists
      removeGlifyLayer();

      // Create glify points layer with proper data format
      const pointsData = entities.map((entity) => [
        entity.coordinates[0], // latitude
        entity.coordinates[1], // longitude
      ]);

      // Color function for facility types (neon colors) - using DB values
      const colorFn = (index: number) => {
        const type = entities[index]?.type?.toLowerCase();
        switch (type) {
          // Manufacturing (Cyan)
          case "factory":
          case "facility_complex":
          case "production_line":
          case "refinery":
          case "recycling":
            return { r: 0.0, g: 0.8, b: 1.0 }; // Neon cyan

          // Logistics (Orange/Purple)
          case "warehouse":
            return { r: 1.0, g: 0.4, b: 0.0 }; // Neon orange
          case "distribution_center":
            return { r: 0.6, g: 0.0, b: 1.0 }; // Neon purple
          case "service_center":
            return { r: 0.8, g: 0.2, b: 0.8 }; // Light purple

          // Commercial (Pink/Green)
          case "store":
            return { r: 1.0, g: 0.0, b: 0.5 }; // Neon pink
          case "office":
            return { r: 0.0, g: 1.0, b: 0.4 }; // Neon green
          case "exhibition_booth":
            return { r: 1.0, g: 0.6, b: 0.8 }; // Light pink

          // Technology (Yellow)
          case "research_and_development":
          case "data_center":
            return { r: 1.0, g: 1.0, b: 0.0 }; // Neon yellow

          // Transportation (Blue)
          case "port_or_harbor":
          case "airport":
          case "rail_or_rail_station":
            return { r: 0.0, g: 0.6, b: 1.0 }; // Bright blue

          // Extractive (Brown-orange)
          case "mine":
          case "oil_rig":
            return { r: 0.8, g: 0.4, b: 0.0 }; // Brown-orange

          // Energy (Red)
          case "power_plant":
            return { r: 1.0, g: 0.2, b: 0.2 }; // Bright red

          // Military (Dark green)
          case "military":
            return { r: 0.2, g: 0.5, b: 0.2 }; // Dark green

          // Other
          default:
            return { r: 0.5, g: 0.5, b: 0.5 }; // Gray
        }
      };

      try {
        const layer = L.glify.points({
          map,
          data: pointsData,
          latitudeKey: 0,
          longitudeKey: 1,
          size: 10,
          color: colorFn,
          opacity: 0.85,
          sensitivity: 2, // Improves rendering on high-DPI displays
          click: (e: any, feature: any) => {
            // Get click coordinates
            const clickLat = e.latlng.lat;
            const clickLng = e.latlng.lng;

            // Calculate threshold based on zoom level (more precise at higher zoom)
            const zoom = map.getZoom();
            const threshold = 5 / Math.pow(2, zoom); // Decreases as zoom increases

            // Find closest point
            let closestIndex = -1;
            let minDistance = Infinity;

            entities.forEach((entity, index) => {
              const lat = entity.coordinates[0];
              const lng = entity.coordinates[1];
              const distance = Math.sqrt(
                Math.pow(lat - clickLat, 2) + Math.pow(lng - clickLng, 2),
              );

              if (distance < minDistance) {
                minDistance = distance;
                closestIndex = index;
              }
            });

            // Only trigger if click is close enough
            if (closestIndex >= 0 && minDistance < threshold) {
              const entity = entities[closestIndex];
              pointClickedRef.current = true; // Mark that a point was clicked
              onEntityClick(entity);
              // Reset flag after a short delay to allow map click to check it
              setTimeout(() => {
                pointClickedRef.current = false;
              }, 100);
            }
          },
          hover: (e: any, feature: any) => {
            // Get hover coordinates
            const hoverLat = e.latlng.lat;
            const hoverLng = e.latlng.lng;

            // Find closest point
            let closestIndex = -1;
            let minDistance = Infinity;

            entities.forEach((entity, index) => {
              const lat = entity.coordinates[0];
              const lng = entity.coordinates[1];
              const distance = Math.sqrt(
                Math.pow(lat - hoverLat, 2) + Math.pow(lng - hoverLng, 2),
              );

              if (distance < minDistance) {
                minDistance = distance;
                closestIndex = index;
              }
            });

            // Only show tooltip if hover is close enough (within ~0.3 degrees)
            if (closestIndex >= 0 && minDistance < 0.3) {
              const entity = entities[closestIndex];

              // Create or update tooltip
              if (!tooltipRef.current) {
                tooltipRef.current = L.popup({
                  closeButton: false,
                  className: "glify-tooltip",
                  offset: [0, -5],
                });
              }

              tooltipRef.current
                .setLatLng([entity.coordinates[0], entity.coordinates[1]])
                .setContent(
                  `
                <div style="
                  min-width: 180px;
                  color: #ffffff;
                  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                  line-height: 1.5;
                ">
                  <strong style="color: #1976d2; font-size: 14px; font-weight: 600;">
                    ${entity.name}
                  </strong>
                  <br />
                  <em style="color: #90caf9; font-style: normal; font-size: 12px;">
                    ${entity.facility_type?.join(", ") || "Unknown"}
                  </em>
                  <br />
                  <span style="color: #e0e0e0; font-size: 12px;">
                    ${entity.region || "Unknown region"}
                  </span>
                  <br />
                  <small style="color: #b0b0b0; font-size: 11px;">
                    Click for details
                  </small>
                </div>
              `,
                )
                .openOn(map);
            }
          },
          hoverOff: (e: any) => {
            // Close tooltip when hover ends
            if (tooltipRef.current) {
              map.closePopup(tooltipRef.current);
            }
          },
        });
        // Guard _redraw against a null _map. leaflet.glify schedules _redraw via
        // requestAnimationFrame; when the layer is removed (e.g. on a search /
        // filter re-render) Leaflet nulls _map and onRemove does NOT cancel the
        // queued frame, so it fires post-removal and crashes with
        // "Cannot read properties of null (reading 'getSize')". The crashing
        // _redraw lives on the underlying CanvasOverlay (layer.layer), not the
        // Points wrapper (layer) — guard both.
        const guardRedraw = (obj: any) => {
          if (obj && obj._redraw) {
            const originalRedraw = obj._redraw.bind(obj);
            obj._redraw = () => {
              if (obj._map) {
                originalRedraw();
              } else {
                // _redraw normally clears _frame; keep that invariant when
                // skipping so a re-added layer can schedule frames again.
                obj._frame = null;
              }
            };
          }
        };
        guardRedraw(layer);
        guardRedraw(layer.layer);
        glifyLayerRef.current = layer;
      } catch {
        // Harmless — map container may not be fully laid out yet
      }
    };

    // Use whenReady + rAF to ensure the map container is rendered and sized
    const frameId = { current: 0 };
    map.whenReady(() => {
      frameId.current = requestAnimationFrame(initGlify);
    });

    return () => {
      unmounted = true;
      cancelAnimationFrame(frameId.current);
      removeGlifyLayer();
    };
  }, [map, entities, onEntityClick]);

  return null;
};

interface InteractiveMapProps {
  entities: FacilityData[];
  onEntityClick: (entity: FacilityData) => void;
  onMapClick?: () => void;
  searchTerm: string;
  selectedRegion: string;
  selectedTypes: string[];
  selectedNaicsCodes: string[];
  selectedHsCodesInput: string[];
  selectedHsCodesOutput: string[];
  selectedFacilityOwners: string[];
  selectedFacilityStatuses: string[];
  selectedSuppliers: string[];
  selectedCustomers: string[];
}

// Component to handle map events - removed, using MapClickHandler instead

const InteractiveMap: React.FC<InteractiveMapProps> = ({
  entities,
  onEntityClick,
  onMapClick,
  searchTerm,
  selectedRegion,
  selectedTypes,
  selectedNaicsCodes,
  selectedHsCodesInput,
  selectedHsCodesOutput,
  selectedFacilityOwners,
  selectedFacilityStatuses,
  selectedSuppliers,
  selectedCustomers,
}) => {
  // Add CSS for glify tooltips
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .glify-tooltip .leaflet-popup-content-wrapper {
        background: #1e1e1e !important;
        color: #ffffff !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5) !important;
      }
      .glify-tooltip .leaflet-popup-content {
        margin: 12px 16px !important;
      }
      .glify-tooltip .leaflet-popup-tip {
        background: #1e1e1e !important;
        box-shadow: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // All filtering is now handled by the backend API
  // Just display all entities passed from parent component
  const filteredEntities = entities;

  // Custom dark tile layer for the map.
  // Esri World Dark Gray Canvas — no API key, unlike CARTO's dark_all, which
  // since Aug 2026 watermarks unkeyed tiles. Note {z}/{y}/{x} ordering: Esri
  // is not XYZ-ordered. The base carries no place labels, so the Reference
  // layer is stacked on top of it below.
  const darkTileUrl =
    "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}";
  const darkLabelsUrl =
    "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}";
  const attribution =
    '&copy; <a href="https://www.esri.com/">Esri</a>, HERE, Garmin, &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

  // Colour tuning for the basemap. Esri's dark gray runs lighter and warmer
  // than CARTO's near-black, so we cool it toward blue-black.
  //
  // `sepia` is load-bearing rather than cosmetic: the source tiles are nearly
  // grey, and hue-rotate on a desaturated image is a no-op. sepia() injects a
  // hue for hue-rotate to steer, and saturate() sets how far it carries.
  //
  // Scoped to the basemap pane alone (below), so it never touches the label
  // layer or the WebGL points overlay.
  const basemapFilter =
    "brightness(0.9) sepia(0.35) hue-rotate(175deg) saturate(1.8) contrast(1.05)";

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        backgroundColor: "#262626",
        position: "relative",
        "& .leaflet-container": {
          backgroundColor: "#262626",
          cursor: "default !important",
        },
        "& .leaflet-basemap-pane": {
          filter: basemapFilter,
        },
        "& .leaflet-grab": {
          cursor: "default !important",
        },
        "& .leaflet-dragging .leaflet-grab": {
          cursor: "default !important",
        },
      }}
    >
      <MapContainer
        center={[40.0, 0.0]} // Center on world view
        zoom={2}
        style={{ height: "100%", width: "100%", backgroundColor: "#262626" }}
        zoomControl={false}
        scrollWheelZoom={true}
        minZoom={2.5}
        maxZoom={18}
        maxBounds={[
          [-90, -180],
          [90, 180],
        ]}
        maxBoundsViscosity={1.0}
        attributionControl={false}
      >
        {/* Base and labels live in separate panes so the colour filter can
            apply to the imagery without washing out the label text. */}
        <Pane name="basemap" style={{ zIndex: 200 }}>
          <TileLayer
            url={darkTileUrl}
            attribution={attribution}
            maxNativeZoom={16}
          />
        </Pane>
        <Pane name="basemap-labels" style={{ zIndex: 250 }}>
          <TileLayer url={darkLabelsUrl} maxNativeZoom={16} />
        </Pane>

        {/* WebGL Points Layer */}
        <GlifyPointsLayer
          entities={filteredEntities}
          onEntityClick={onEntityClick}
        />
        {onMapClick && <MapClickHandler onMapClick={onMapClick} />}
        <CustomZoomControls />
      </MapContainer>

      {/* Map Legend */}
      <MapLegend />
    </Box>
  );
};
export default InteractiveMap;
